import { createContext, useContext, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';

export type OrgType = 'BUYER' | 'SUPPLIER' | 'PLATFORM';
export interface Me {
  authenticated: boolean;
  pendingOtp?: boolean;
  user?: { id: string; email: string; fullName: string; locale: string; mfaEnabled: boolean };
  org?: { id: string; type: OrgType; status: string; code: string; displayName: string; defaultCurrency: string } | null;
  roleCode?: string | null;
  permissions?: string[];
  memberships?: { orgId: string; orgName: string; orgType: OrgType; orgStatus: string; roleName: string; roleCode: string; status: string }[];
}

interface AuthCtx {
  me: Me | undefined;
  loading: boolean;
  can: (perm: string) => boolean;
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
}
const Ctx = createContext<AuthCtx>(null!);

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['me'], queryFn: () => api.get<Me>('/auth/me'), staleTime: 60_000, retry: false });
  const value: AuthCtx = {
    me: q.data,
    loading: q.isLoading,
    can: (perm) => !!q.data?.permissions?.includes(perm),
    refresh: async () => {
      await qc.invalidateQueries();
      await q.refetch();
    },
    signOut: async () => {
      await api.post('/auth/logout').catch(() => {});
      qc.clear();
      window.location.href = '/login';
    },
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useAuth = () => useContext(Ctx);

/** Narrowed access for pages rendered inside the authenticated shell. */
export function useSession() {
  const { me, can, refresh, signOut } = useAuth();
  return { user: me!.user!, org: me!.org!, roleCode: me!.roleCode!, can, refresh, signOut, memberships: me!.memberships ?? [] };
}
